import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: 'error' | 'primary' | 'secondary' | 'success' | 'warning';
  loading?: boolean;
}

/**
 * Confirmation dialog component for critical actions
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmColor = 'primary',
  loading = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle 
        id="confirm-dialog-title"
        sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
      >
        {title}
      </DialogTitle>
      <DialogContent>
        <DialogContentText 
          id="confirm-dialog-description"
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          {message}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 } }}>
        <Button
          onClick={onCancel}
          color="inherit"
          size={isMobile ? 'small' : 'medium'}
          disabled={loading}
        >
          {cancelText}
        </Button>
        <Button 
          onClick={onConfirm} 
          color={confirmColor} 
          variant="contained" 
          autoFocus
          size={isMobile ? 'small' : 'medium'}
          disabled={loading}
        >
          {loading ? `${confirmText}…` : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
